import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { getOrderCardImageSrc } from '../utils/orderCardImageSrc';

const STAGGER_MS = 130;
const FLIGHT_MS = 920;
const CARD_BASE_WIDTH_PX = 220;
const START_SCALE = 0.09;

function easeOutCubic(t) {
  return 1 - (1 - t) ** 3;
}

/**
 * When Tactical Loadout (strategem 114) lands in completedOrders, animate two weapon thumbnails
 * from the center “box” outward: to the caster’s hand (viewer POV) or off the top when the opponent cast it.
 */
export function useTacticalLoadoutFx(gameState, userId) {
  const [fxSession, setFxSession] = useState(null);
  const isFirstRef = useRef(true);
  const prevKeysRef = useRef(new Set());
  const queueRef = useRef([]);

  useEffect(() => {
    if (!gameState?.state) return undefined;

    if (isFirstRef.current) {
      isFirstRef.current = false;
      const allPlayerIds = gameState.players || Object.keys(gameState.state);
      allPlayerIds.forEach((pid) => {
        const player = gameState.state[pid];
        player?.completedOrders?.forEach((s) => {
          if (s?.type === 'Strategem' && Number(s.id) === 114 && s.instanceId) {
            prevKeysRef.current.add(`${pid}_${s.instanceId}`);
          }
        });
      });
      return undefined;
    }

    const newSessions = [];
    const allPlayerIds = gameState.players || Object.keys(gameState.state);
    allPlayerIds.forEach((playerId) => {
      const player = gameState.state[playerId];
      player?.completedOrders?.forEach((s) => {
        if (s?.type === 'Strategem' && Number(s.id) === 114 && s.instanceId) {
          const key = `${playerId}_${s.instanceId}`;
          if (!prevKeysRef.current.has(key)) {
            prevKeysRef.current.add(key);
            const weapons = Array.isArray(s.tacticalLoadoutDrawFx?.weapons)
              ? s.tacticalLoadoutDrawFx.weapons
              : [];
            newSessions.push({
              key,
              playerId,
              isSelf: userId != null && String(playerId) === String(userId),
              weapons,
            });
          }
        }
      });
    });

    if (newSessions.length > 0) {
      newSessions.forEach((sess) => queueRef.current.push(sess));
      setFxSession((curr) => {
        if (curr) return curr;
        return queueRef.current.shift() ?? null;
      });
    }

    return undefined;
  }, [gameState, userId]);

  const completeFxSession = useCallback(() => {
    setFxSession(queueRef.current.shift() ?? null);
  }, []);

  return { fxSession, completeFxSession };
}

function TacticalLoadoutFlyingWeapon({ weapon, index, isSelf, onDone }) {
  const [style, setStyle] = useState(() => ({
    position: 'fixed',
    left: typeof window !== 'undefined' ? window.innerWidth / 2 : 0,
    top: typeof window !== 'undefined' ? window.innerHeight / 2 : 0,
    width: CARD_BASE_WIDTH_PX,
    transform: `translate(-50%, -50%) scale(${START_SCALE})`,
    zIndex: 100005 + index,
    pointerEvents: 'none',
    opacity: 1,
  }));

  useLayoutEffect(() => {
    const stagger = STAGGER_MS;
    const duration = FLIGHT_MS;
    const delayMs = index * stagger;

    const startX = window.innerWidth / 2;
    const startY = window.innerHeight / 2;

    let cachedEnd = null;
    let startTime = null;
    let rafId = 0;

    const resolveFallbackSelfEnd = () => ({
      x: window.innerWidth / 2,
      y: window.innerHeight - 110,
      scaleNorm: 1,
    });

    const resolveOpponentEnd = () => {
      const spread = (index - 0.5) * 110;
      return {
        x: window.innerWidth / 2 + spread,
        y: -75,
        scaleNorm: 1.05,
      };
    };

    const tick = (now) => {
      if (startTime === null) {
        startTime = now + delayMs;
      }
      if (now < startTime) {
        rafId = requestAnimationFrame(tick);
        return;
      }

      if (isSelf) {
        if (!cachedEnd) {
          const el = document.querySelector(`[data-instance-id="${weapon.instanceId}"]`);
          if (el) {
            const r = el.getBoundingClientRect();
            cachedEnd = {
              x: r.left + r.width / 2,
              y: r.top + r.height / 2,
              scaleNorm: Math.max(0.4, Math.min(1.65, r.width / CARD_BASE_WIDTH_PX)),
            };
          }
        }
      } else if (!cachedEnd) {
        cachedEnd = resolveOpponentEnd();
      }

      const end = cachedEnd || (isSelf ? resolveFallbackSelfEnd() : resolveOpponentEnd());

      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      const e = easeOutCubic(t);

      const x = startX + (end.x - startX) * e;
      const y = startY + (end.y - startY) * e;
      const endScale = end.scaleNorm;
      const scale = START_SCALE + (endScale - START_SCALE) * e;

      setStyle({
        position: 'fixed',
        left: x,
        top: y,
        width: CARD_BASE_WIDTH_PX,
        transform: `translate(-50%, -50%) scale(${scale})`,
        zIndex: 100005 + index,
        pointerEvents: 'none',
        opacity: 1,
      });

      if (t < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        onDone();
      }
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [weapon.instanceId, index, isSelf, onDone]);

  const src = getOrderCardImageSrc(weapon);

  return (
    <img
      alt=""
      src={src}
      draggable={false}
      style={{
        ...style,
        boxShadow: '0 14px 32px rgba(0, 0, 0, 0.5)',
        borderRadius: 8,
      }}
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = '/images/default.jpg';
      }}
    />
  );
}

/** Full-screen FX: center box pulse + two weapon cards flying to hand or off-screen top. */
export function TacticalLoadoutFxOverlay({ session, onComplete }) {
  const doneCountRef = useRef(0);
  const total = session?.weapons?.length ?? 0;

  useLayoutEffect(() => {
    if (session?.key) {
      doneCountRef.current = 0;
    }
  }, [session?.key]);

  useLayoutEffect(() => {
    if (session && total === 0) {
      onComplete();
    }
  }, [session, total, onComplete]);

  const handleOneDone = useCallback(() => {
    doneCountRef.current += 1;
    if (doneCountRef.current >= total) {
      doneCountRef.current = 0;
      onComplete();
    }
  }, [total, onComplete]);

  if (!session || total === 0) return null;

  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 100004,
      }}
    >
      <style>
        {`
          @keyframes tacticalLoadoutBoxPulse {
            0% { opacity: 0; transform: translate(-50%, -50%) scale(0.35); }
            18% { opacity: 0.95; transform: translate(-50%, -50%) scale(1); }
            100% { opacity: 0; transform: translate(-50%, -50%) scale(1.05); }
          }
        `}
      </style>
      <img
        src="/images/loadout_target.png"
        alt=""
        style={{
          position: 'fixed',
          left: '50%',
          top: '50%',
          width: 'min(44vw, 320px)',
          height: 'auto',
          objectFit: 'contain',
          transform: 'translate(-50%, -50%)',
          animation: 'tacticalLoadoutBoxPulse 1s ease-out forwards',
          filter: 'drop-shadow(0 8px 24px rgba(0, 0, 0, 0.45))',
          zIndex: 100004,
        }}
      />
      {session.weapons.map((w, i) => (
        <TacticalLoadoutFlyingWeapon
          key={w.instanceId != null ? String(w.instanceId) : `w-${i}`}
          weapon={w}
          index={i}
          isSelf={session.isSelf}
          onDone={handleOneDone}
        />
      ))}
    </div>
  );
}
